/**
 * MoonStepFilledView
 *
 * Compact summary of completed step data. EMBER-styled.
 * Ported from olly's StepFilledView.
 */

import { View } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { Check } from "lucide-react-native";
import type {
  TreatmentStepProgress,
  LocalMedication,
} from "@louise/care/treatment-phases";
import type { TreatmentStepConfig } from "@src/packages/homepage/sections/TreatmentProgressModal";
import { EMBER } from "../../../../../onboarding/theme";
import MedRow from "../../rows/MedRow";
import {
  deriveRecurrence,
  type MedForm,
  type MedicationScheduleItem,
} from "../../schedule.types";
import { getMedicationByBrandName } from "@src/packages/content";
import { useTranslations } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";

// Map intakeType → form icon
const INTAKE_TO_FORM: Record<string, MedForm> = {
  subcutaneous: "vial",
  intramuscular: "vial",
  intravenous: "vial",
  injection: "vial",
  oral: "pill",
  vaginal: "suppository",
  topical: "suppository",
  patch: "pill",
};

// Map content-database form names → MedForm (fallback when intakeType is unknown)
const CONTENT_FORM_MAP: Record<string, MedForm> = {
  pen: "pen",
  vial: "vial",
  multi_vial: "vial",
  pre_filled_pen: "pen",
  pre_filled_syringe: "vial",
  injection_solution: "vial",
  lyophilized_powder: "vial",
  nasal_spray: "vial",
  pill: "pill",
  tablet: "pill",
  capsule: "pill",
  oral_solution: "pill",
  sublingual_tablet: "pill",
  transdermal_patch: "pill",
  patch: "pill",
  cream: "pill",
  other: "pill",
  suppository: "suppository",
  vaginal_gel: "suppository",
  vaginal_suppository: "suppository",
};

function resolveFormFromContentDb(name: string): MedForm | undefined {
  const content = getMedicationByBrandName(name);
  if (!content) return undefined;
  const firstForm = (content.body as { commonForms?: string[] })
    .commonForms?.[0];
  return firstForm ? (CONTENT_FORM_MAP[firstForm] ?? undefined) : undefined;
}

function localMedToScheduleItems(m: LocalMedication): MedicationScheduleItem[] {
  // #3946: a medication with multiple reminder times must render one row per
  // time, matching the calendar normaliser. The hub used to take `timeTaken[0]`
  // only, so the 18:00 dose of a twice-daily med was silently dropped from the
  // treatment step and the patient couldn't tick it.
  const dose = m.dose != null ? `${m.dose} ${m.doseUnit ?? ""}`.trim() : "";
  const intakeType = m.intakeType?.toLowerCase() ?? "";
  const form: MedForm =
    INTAKE_TO_FORM[intakeType] ?? resolveFormFromContentDb(m.name) ?? "pill";
  const times = m.timeTaken && m.timeTaken.length > 0 ? m.timeTaken : ["09:00"];

  return times.map((time) => {
    const hour = parseInt(time);
    const timing: "morning" | "evening" | "as-needed" =
      hour >= 12 ? "evening" : "morning";
    return {
      type: "medication",
      id: m.id,
      name: m.name || "Unknown",
      dosage: dose,
      form,
      route: intakeType
        ? intakeType.charAt(0).toUpperCase() + intakeType.slice(1)
        : "",
      time,
      timing,
      color: EMBER.colors.textMid,
      recurrence: deriveRecurrence({
        startDate: m.startDate,
        endDate: m.endDate,
      }),
      brandName: m.name || undefined,
      userContext: {
        dosage: dose || undefined,
        route: intakeType || undefined,
        // The administration method she recorded, which `form` above is not:
        // that one has four values and picks an illustration. The injection
        // coach reads this so it does not ask again. See `LocalMedication.form`.
        selectedForm: m.form ?? undefined,
        startDate: m.startDate ?? undefined,
        endDate: m.endDate,
        notes: m.notes ?? undefined,
      },
    };
  });
}

interface MoonStepFilledViewProps {
  step: TreatmentStepConfig;
  stepProgress: TreatmentStepProgress;
  linkedMedications: LocalMedication[];
  onEditMedication?: (med: MedicationScheduleItem) => void;
}

/**
 * The human label for a step field's option value.
 *
 * These arrive as the API's own enum — `partner`, `fresh` — and this summary was
 * pushing them straight in, so a French reader got "partner · fresh" from a card
 * whose own modal says "Partenaire · Frais". `care.treatment_flows.options` is
 * the namespace that modal reads, so the two now agree.
 *
 * Falls back to the raw value rather than trusting the lookup: `t` returns the
 * key path when an entry is missing, and an untranslated enum is a better thing
 * to put in front of a patient than `care.treatment_flows.options.something`.
 */
function optionLabel(
  value: string | number | boolean,
  t: ReturnType<typeof useTranslations>,
): string {
  const raw = String(value);
  const key = `care.treatment_flows.options.${raw}`;
  const label = t(key);
  return label === key ? raw : label;
}

function formatDate(
  isoString: string | null | undefined,
  locale: string,
): string | null {
  if (!isoString || typeof isoString !== "string") return null;
  try {
    /**
     * A bare `YYYY-MM-DD` is parsed as midnight UTC, so anywhere east of
     * Greenwich `new Date()` lands on a non-zero local hour and the branch below
     * appended an hour she never entered — "1 Aug 02:00" over a date-only value.
     * A date with no time is a day, so its parts are read as the local day they
     * name. A value that carries a time is left alone: the picker writes
     * `toISOString()` from a local Date, and converting it back is the point.
     */
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoString);
    const d = dateOnly
      ? new Date(
          Number(dateOnly[1]),
          Number(dateOnly[2]) - 1,
          Number(dateOnly[3]),
        )
      : new Date(isoString);
    if (isNaN(d.getTime())) return null;
    const dateStr = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
    }).format(d);
    if (
      isoString.includes("T") &&
      (d.getHours() !== 0 || d.getMinutes() !== 0)
    ) {
      const timeStr = new Intl.DateTimeFormat(locale, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(d);
      return `${dateStr} ${timeStr}`;
    }
    return dateStr;
  } catch {
    return null;
  }
}

function formatDateTime(
  isoString: string | null | undefined,
  locale: string,
): string | null {
  if (!isoString || typeof isoString !== "string") return null;
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return null;
    const dateStr = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
    }).format(d);
    const timeStr = new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
    return `${dateStr} ${timeStr}`;
  } catch {
    return null;
  }
}

function renderProcedureContent(
  step: TreatmentStepConfig,
  data: Record<string, string | number | boolean | null>,
  t: ReturnType<typeof useTranslations>,
  locale: string,
): string {
  const parts: string[] = [];
  const stepId = step.id;

  if (stepId === "egg_retrieval") {
    const eggs = data.eggsCollected;
    const date = formatDate(data.procedureDate as string, locale);
    const notes = data.notes;
    const mature = data.matureEggs;
    const frozen = data.eggsFrozen;
    if (eggs != null) {
      parts.push(
        t(
          Number(eggs) === 1
            ? "care.treatment_hub.step_filled.eggs_collected_one"
            : "care.treatment_hub.step_filled.eggs_collected_other",
          { params: { count: String(eggs) } },
        ),
      );
    }
    if (mature != null) {
      parts.push(
        t("care.treatment_hub.step_filled.mature_count", {
          params: { count: String(mature) },
        }),
      );
    }
    if (frozen != null) {
      parts.push(
        t("care.treatment_hub.step_filled.frozen_count", {
          params: { count: String(frozen) },
        }),
      );
    }
    if (date && parts.length === 0) parts.push(date);
    else if (date) parts.push(date);
    if (notes) parts.push(String(notes));
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (stepId === "freezing") {
    const mature = data.matureEggs;
    const frozen = data.eggsFrozen;
    const location = data.storageLocation;
    /**
     * Sperm freezing counts vials and dates the freeze; this branch only knew
     * how to read the egg-freezing fields, so the step fell through to the
     * generic field loop below and reported "Freeze Date:
     * 2026-08-01T06:00:00.000Z" — the picker stores `toISOString()`, and that
     * loop prints whatever it is handed.
     */
    const vials = data.vialsFrozen;
    if (vials != null && vials !== "") {
      parts.push(
        t(
          Number(vials) === 1
            ? "care.treatment_hub.step_filled.vials_frozen_one"
            : "care.treatment_hub.step_filled.vials_frozen_other",
          { params: { count: String(vials) } },
        ),
      );
    }
    const freezeDate = formatDate(data.freezeDate as string, locale);
    if (freezeDate) parts.push(freezeDate);
    if (mature != null) {
      parts.push(
        t("care.treatment_hub.step_filled.mature_count", {
          params: { count: String(mature) },
        }),
      );
    }
    if (frozen != null) {
      parts.push(
        t("care.treatment_hub.step_filled.frozen_count", {
          params: { count: String(frozen) },
        }),
      );
    }
    if (location) parts.push(String(location));
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (stepId === "fertilization") {
    const fertilized = data.eggsFertilized;
    const method = data.fertilizationMethod;
    const embryosFrozen = data.embryosFrozen;
    const notes = data.notes;
    if (fertilized != null) {
      parts.push(
        t("care.treatment_hub.step_filled.fertilized_count", {
          params: { count: String(fertilized) },
        }),
      );
    }
    if (method) parts.push(String(method).toUpperCase());
    if (embryosFrozen != null) {
      parts.push(
        t("care.treatment_hub.step_filled.frozen_count", {
          params: { count: String(embryosFrozen) },
        }),
      );
    }
    if (notes) parts.push(String(notes));
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (stepId === "embryo_development") {
    const grade = data.embryoGrade;
    const frozen = data.embryosFrozen;
    if (grade) {
      parts.push(
        t("care.treatment_hub.step_filled.grade_label", {
          params: { grade: String(grade) },
        }),
      );
    }
    if (frozen != null) {
      parts.push(
        t("care.treatment_hub.step_filled.frozen_count", {
          params: { count: String(frozen) },
        }),
      );
    }
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (stepId === "transfer") {
    const transferred = data.embryosTransferred;
    const date = formatDate(data.transferDate as string, locale);
    if (transferred != null) {
      parts.push(
        t(
          transferred === 1
            ? "care.treatment_hub.step_filled.embryo_transferred_one"
            : "care.treatment_hub.step_filled.embryo_transferred_other",
          { params: { count: String(transferred) } },
        ),
      );
    }
    if (date) parts.push(date);
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (stepId === "sperm_collection") {
    const source = data.spermSource;
    const type = data.spermType;
    // The date the sample was actually given. It round-trips already — the step
    // field writes `spermiograms[0].date`/`.time` and `usePhases` reads it back
    // into `collectionDate` — but this summary never read it, so recording it
    // changed nothing on the card beyond the phase's own start date. Every other
    // procedure step names its date here; this one is the omission.
    const date = formatDate(data.collectionDate as string, locale);
    if (source) parts.push(optionLabel(source, t));
    if (type) parts.push(optionLabel(type, t));
    if (date) parts.push(date);
    if (data.semenSpecimenId) {
      parts.push(t("care.treatment_hub.step_filled.specimen_linked"));
    }
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  /**
   * The semen analysis, in the words the report uses.
   *
   * Without this the step fell through to the generic field loop below, which
   * prints `field.label: value` — so a French reader saw "Concentration (M/mL):
   * 18" from a dialogue that had asked her in French, and five figures read as
   * a list of labels rather than a result.
   */
  if (stepId === "analysis") {
    const measurement = (
      key: string,
      value: string | number | boolean | null | undefined,
    ) => {
      if (value == null || value === "") return;
      parts.push(t(key, { params: { value: String(value) } }));
    };
    measurement(
      "care.treatment_hub.step_filled.concentration_per_ml",
      data.concentration,
    );
    measurement(
      "care.treatment_hub.step_filled.total_count_in_millions",
      data.totalCount,
    );
    measurement(
      "care.treatment_hub.step_filled.percent_motile",
      data.motilityPercent,
    );
    measurement(
      "care.treatment_hub.step_filled.percent_progressively_motile",
      data.progressiveMotilityPercent,
    );
    measurement(
      "care.treatment_hub.step_filled.percent_normal_forms",
      data.morphologyPercent,
    );
    // The unit the record carries, not the one the field happens to ask for.
    const volumeUnit =
      typeof data.volumeUnit === "string" && data.volumeUnit.trim()
        ? data.volumeUnit
        : null;
    if (data.volumeMl != null && data.volumeMl !== "") {
      parts.push(
        volumeUnit && volumeUnit.toLowerCase() !== "ml"
          ? t("care.treatment_hub.step_filled.volume_with_unit", {
              params: { value: String(data.volumeMl), unit: volumeUnit },
            })
          : t("care.treatment_hub.step_filled.volume_in_ml", {
              params: { value: String(data.volumeMl) },
            }),
      );
    }
    if (data.notes) parts.push(String(data.notes));
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (stepId === "insemination") {
    const date = formatDate(data.procedureDate as string, locale);
    if (date) parts.push(t("care.treatment_hub.step_filled.iui_completed"));
    if (date) parts.push(date);
    if (data.semenSpecimenId) {
      parts.push(t("care.treatment_hub.step_filled.specimen_linked"));
    }
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (step.fields) {
    for (const field of step.fields) {
      const val = data[field.name];
      if (val == null || val === "" || field.type === "textarea") continue;
      // A date or datetime field is stored as an ISO string, so printing the
      // value as it arrives put `2026-08-01T06:00:00.000Z` in front of a
      // patient. Every step that names its own date formats it; this is the
      // fallback for the ones that have not been given a branch above.
      if (field.type === "date" || field.type === "datetime") {
        // A value this cannot read as a date still gets shown. Dropping it
        // made the entry vanish from the summary, which reads as "nothing
        // recorded" over something she did record — a value she can see is
        // wrong is more use to her than an absence she cannot explain.
        const formatted = formatDate(String(val), locale);
        parts.push(`${field.label}: ${formatted ?? String(val)}`);
        continue;
      }
      const customVal = data[`${field.name}Custom`];
      const display =
        val === "Other" && customVal != null && customVal !== ""
          ? customVal
          : val;
      // The label no longer names the unit; the field carries it.
      const unit = field.unit
        ? field.unit === "%"
          ? "%"
          : ` ${field.unit}`
        : "";
      parts.push(`${field.label}: ${display}${unit}`);
    }
  }

  return parts.length > 0
    ? parts.join(" \u00B7 ")
    : t("care.treatment_hub.step_filled.completed");
}

function renderTriggerContent(
  data: Record<string, string | number | boolean | null>,
  t: ReturnType<typeof useTranslations>,
  locale: string,
): string {
  const triggerTime = data.triggerTime as string | null;
  const triggerDate = data.triggerDate as string | null;
  const med1 =
    (data.triggerMedicationCustom as string | null) ||
    (data.triggerMedication as string | null) ||
    null;
  const med2Raw = data.triggerMedication2 as string | null;
  const med2 =
    (data.triggerMedicationCustom2 as string | null) ||
    (med2Raw && med2Raw !== "__pending__" ? med2Raw : null) ||
    null;
  const triggerMed = med1 && med2 ? `${med1} + ${med2}` : med1 || null;

  const parts: string[] = [];
  if (triggerMed) parts.push(triggerMed);
  if (triggerTime || triggerDate) {
    const dateStr = formatDateTime(triggerDate ?? triggerTime, locale);
    if (dateStr) parts.push(dateStr);
  }

  if (parts.length > 0) {
    return `${t("care.treatment_hub.step_filled.trigger_confirmed")} \u00B7 ${parts.join(" \u00B7 ")}`;
  }

  return t("care.treatment_hub.step_filled.trigger_confirmed");
}

export function MoonStepFilledView({
  step,
  stepProgress,
  linkedMedications,
  onEditMedication,
}: MoonStepFilledViewProps) {
  const t = useTranslations();
  const { locale } = useLocale();
  const data = stepProgress.data ?? {};

  let summaryText: string;

  switch (step.contentType) {
    case "medications":
      summaryText =
        linkedMedications.length > 0
          ? t(
              linkedMedications.length === 1
                ? "care.treatment_hub.step_filled.medications_one"
                : "care.treatment_hub.step_filled.medications_other",
              { params: { count: String(linkedMedications.length) } },
            )
          : t("care.treatment_hub.step_filled.completed");
      break;
    case "procedure":
      summaryText = renderProcedureContent(step, data, t, locale);
      break;
    case "trigger":
      summaryText = renderTriggerContent(data, t, locale);
      break;
    case "waiting":
      summaryText = t("care.treatment_hub.step_filled.waiting_in_progress");
      break;
    default:
      summaryText = t("care.treatment_hub.step_filled.completed");
  }

  if (step.contentType === "medications" && linkedMedications.length > 0) {
    return (
      <View style={{ gap: 0, marginLeft: -16 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            color: EMBER.colors.textFaint,
            marginLeft: 16,
            marginBottom: 4,
          }}
        >
          {t(
            linkedMedications.length === 1
              ? "care.treatment_hub.step_filled.medications_one"
              : "care.treatment_hub.step_filled.medications_other",
            { params: { count: String(linkedMedications.length) } },
          )}
        </Text>
        {(() => {
          // Cap at MED_CAP medications (not reminder rows): one medication
          // with four reminders used to fill the slot and hide every other
          // med, while the "+N" label still said medications.
          const MED_CAP = 5;
          const visibleMeds = linkedMedications.slice(0, MED_CAP);
          const hiddenCount = Math.max(
            0,
            linkedMedications.length - visibleMeds.length,
          );
          const items = visibleMeds.flatMap(localMedToScheduleItems);
          return (
            <>
              {items.map((item, i) => (
                <View
                  key={`${item.id}:${item.time}`}
                  style={{
                    marginTop: i > 0 ? 2 : 0,
                  }}
                >
                  <MedRow item={item} onEditMedication={onEditMedication} />
                </View>
              ))}
              {hiddenCount > 0 && (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    color: EMBER.colors.textFaint,
                    marginLeft: 16,
                    marginTop: 4,
                  }}
                >
                  {t(
                    hiddenCount === 1
                      ? "care.treatment_hub.completion.med_more_medications_one"
                      : "care.treatment_hub.completion.med_more_medications_other",
                    { params: { count: String(hiddenCount) } },
                  )}
                </Text>
              )}
            </>
          );
        })()}
      </View>
    );
  }

  return (
    <View className="flex-row items-center gap-1.5">
      <Check size={12} color={EMBER.colors.textLight} strokeWidth={2} />
      <Text
        className="font-dm-sans"
        style={{ fontSize: 12, color: EMBER.colors.textLight, flex: 1 }}
        numberOfLines={2}
      >
        {summaryText}
      </Text>
    </View>
  );
}
